import { FieldType } from '@databases/model/field';
import {
  type EntryLinkValue,
  type MutableEntryLinkValue,
  type SingleEntryLinkValue,
  type ValueJSON
} from '@databases/model/value';
import * as Y from 'yjs';

import { SynchronizedArray } from '../helpers/synchronized-array.ts';
import { SynchronizedMapElement } from '../helpers/synchronized-map-element.ts';
import { SynchronizedRecord } from '../helpers/synchronized-record.ts';
import { type DisposableElement, type WrappedYType } from '../helpers/types.ts';
import { ValueProperties } from '../y-entry.ts';
import { FieldMapping } from '../y-field.ts';

const EntryLinkValueProperties = {
  entryId: 'e',
  id: 'i'
};

class YSingleEntryLinkValue extends SynchronizedRecord implements WrappedYType {
  static mapper(value: unknown): YSingleEntryLinkValue | undefined {
    if (value instanceof Y.Map) {
      return new YSingleEntryLinkValue(value);
    }
  }

  get entryId(): string {
    return this.getString(EntryLinkValueProperties.entryId, '');
  }

  set entryId(val: string) {
    this.set(EntryLinkValueProperties.entryId, val);
  }

  get id(): string {
    return this.getString(EntryLinkValueProperties.id, '');
  }

  toJSON(): SingleEntryLinkValue {
    return {
      entryId: this.entryId,
      id: this.id
    };
  }

  static fromJSON(value: SingleEntryLinkValue): YSingleEntryLinkValue {
    return new YSingleEntryLinkValue(
      new Y.Map([
        [EntryLinkValueProperties.entryId, value.entryId],
        [EntryLinkValueProperties.id, value.id]
      ])
    );
  }
}

export class YEntryLinkValue
  implements EntryLinkValue, MutableEntryLinkValue, WrappedYType, DisposableElement
{
  private valueElement: SynchronizedMapElement<SynchronizedArray<YSingleEntryLinkValue>>;

  constructor(private input: Y.Map<any>) {
    this.valueElement = this.buildValueElement();
  }

  get isEmpty() {
    return !this.valueElement.value?.toArray().length;
  }

  get type(): FieldType.EntryLink {
    return FieldType.EntryLink;
  }

  get value() {
    return this.valueElement.value?.toArray() ?? [];
  }

  add(value: SingleEntryLinkValue): void {
    this.valueElement.valueRequired.push([YSingleEntryLinkValue.fromJSON(value)]);
  }

  delete(index: number): void {
    this.valueElement.valueRequired.delete(index);
  }

  reorder(index: number, newIndex: number): void {
    const val = this.valueElement.valueRequired.get(index).toJSON();

    this.valueElement.valueRequired.delete(index);
    this.valueElement.valueRequired.insert(newIndex, [
      YSingleEntryLinkValue.fromJSON(val)
    ]);
  }

  setTo(value: SingleEntryLinkValue[]): void {
    const valueRef = Y.Array.from(
      value.map((link) => YSingleEntryLinkValue.fromJSON(link).unwrap())
    );
    this.input.set(ValueProperties.value, valueRef);
    this.valueElement = this.buildValueElement();
  }

  private buildValueElement(): SynchronizedMapElement<
    SynchronizedArray<YSingleEntryLinkValue>
  > {
    return new SynchronizedMapElement<SynchronizedArray<YSingleEntryLinkValue>>(
      this.input,
      ValueProperties.value,
      (yarray) => {
        if (yarray instanceof Y.Array) {
          return new SynchronizedArray(
            yarray as Y.Array<unknown>,
            YSingleEntryLinkValue.mapper
          );
        }
      },
      () => new Y.Array()
    );
  }

  toJSON(): ValueJSON<FieldType.EntryLink, SingleEntryLinkValue[]> {
    return {
      type: this.type,
      value: this.value.map((v) => v.toJSON())
    };
  }

  static fromJSON(json: ValueJSON<FieldType.EntryLink, SingleEntryLinkValue[]>) {
    return new YEntryLinkValue(
      new Y.Map([
        [ValueProperties.type, FieldMapping[json.type]],
        [
          ValueProperties.value,
          Y.Array.from(json.value.map((v) => YSingleEntryLinkValue.fromJSON(v).unwrap()))
        ]
      ])
    );
  }

  unwrap(): Y.AbstractType<any> {
    return this.input;
  }

  dispose(): void {
    this.valueElement.dispose();
  }
}
